import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { IOption } from '../../../../shared/components/forms/search-input/BpSearchInput'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { Controller, useForm } from 'react-hook-form'
import React, { useEffect } from 'react'
import { FilterDrop } from '../../../../shared/components/cards/filter-drop/FilterDrop'
import { BpDatePicker } from '../../../../shared/components/forms/datepicker/BpDatePicker'
import { setCanCloseDropdown } from '../../../../app/store/reducers/AppSlice'
import moment from 'moment/moment'
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_RANGE_FORMAT,
} from '../../../../shared/consts/DefaultRangeFormat.const'
import {
  selectProject,
  selectSerpPresenceFilters,
  setProject,
  setSerpPresenceFilter,
} from '../../model/serpPresenceSlice'
import { ISerpPresenceFilter } from '../../model/types/SERPPresenceFilter.interface'
import {
  IWithSearchParamsProps,
  withSearchParams,
} from '../../../../shared/lib/hoc/WithSearchParams'
import { Trans, useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { AddProjectButton } from '../../../../shared/components/buttons/add-project-button/AddProjectButton'
import { EditCountriesButton } from '../../../../shared/components/buttons/edit-countries-button/EditCountriesButton'
import { FilterChips } from '../../../../shared/components/utility/filter-chips/FilterChips'
import { ActionsSlot } from '../../../../shared/components/utility/actions-slot/ActionsSlot'
import { useMenuLayout } from '../../../../app/layouts/main-layout/hooks/UseMenuLayout'
import { useSearchState } from '../../../../lib/hooks/useSearchState'
import styles from './SerpPresenceFilters.module.scss'
import {
  GetFilterKeywordsApiArg,
  LandingFilterArg,
  useGetFilterAdvertiserLandingQuery,
  useGetFilterCountryQuery,
  useGetFilterEngineQuery,
  useGetFilterKeywordsQuery,
  useGetFilterProjectsQuery,
} from '../../../../app/api'
import { ProjectIcon } from '../../../../shared/components/icons/filters/ProjectIcon'
import { CountryIcon } from '../../../../shared/components/icons/filters/CountryIcon'
import { SearchEngineIcon } from '../../../../shared/components/icons/filters/SearchEngineIcon'
import { KeywordIcon } from '../../../../shared/components/icons/filters/KeywordIcon'
import { UrlIcon } from '../../../../shared/components/icons/filters/UrlIcon'
import { withSelectedOptions } from '../../../../shared/lib/helpers/WithSelectedOptions'
import { renderCountryOption } from '../../../../shared/components/utility/country-option/CountryOption'
import { restoreAppliedValues } from '../../../../shared/lib/helpers/AppliedValues'
import {
  resetFilterFields,
  useRegisterAppliedFilters,
} from '../../../../lib/context/applied-filters/AppliedFiltersProvider'

/** Фильтры, которые сбрасывает Clear filters, и их значения по умолчанию. */
const CLEARABLE_FILTERS: Partial<ISerpPresenceFilter> = {
  country: '',
  keyword: '',
  page_url: '',
}

const Filter = ({ searchParams, setSearchParams }: IWithSearchParamsProps<ISerpPresenceFilter>) => {
  const { t } = useTranslation()
  const { menuLayout } = useMenuLayout()
  const filters = useAppSelector<ISerpPresenceFilter>(selectSerpPresenceFilters)
  const { country, engine_id, project, dateRange, keyword, page_url } = filters
  const [search, setSearch] = useSearchState({ keyword: '', page_url: '' })
  const { is_demo: isDemo } = useAppSelector(selectProject)
  const dispatch = useAppDispatch()

  const {
    getValues,
    setValue,
    control,
    formState: { defaultValues },
  } = useForm<ISerpPresenceFilter>({
    mode: 'onChange',
    defaultValues: {
      country,
      engine_id,
      project,
      dateRange,
      keyword,
      page_url,
      ...searchParams,
    },
  })

  useEffect(() => {
    applyFilter()
  }, [])

  const {
    data: projects,
    isFetching: projectsFetching,
    isLoading: projectsLoading,
  } = useGetFilterProjectsQuery({})

  const {
    data: countries,
    isFetching: countriesFetching,
    isLoading: countriesLoading,
  } = useGetFilterCountryQuery({ projectId: project ? +project : undefined }, { skip: !project })

  const {
    data: engines,
    isFetching: enginesFetching,
    isLoading: enginesLoading,
  } = useGetFilterEngineQuery({ projectId: project ? +project : undefined }, { skip: !project })

  const {
    data: keywords,
    isFetching: keywordsFetching,
    isLoading: keywordsLoading,
  } = useGetFilterKeywordsQuery(
    {
      projectId: project,
      search: search.keyword,
    } as unknown as GetFilterKeywordsApiArg,
    { skip: !project },
  )

  const {
    data: landings,
    isFetching: landingsFetching,
    isLoading: landingsLoading,
  } = useGetFilterAdvertiserLandingQuery(
    {
      projectId: project ? +project : undefined,
      search: search.page_url,
      date: dateRange,
    } as LandingFilterArg,
    { skip: !project },
  )

  const applyFilter = () => {
    const values = getValues()
    dispatch(setSerpPresenceFilter(values))
    if (setSearchParams) setSearchParams(values)
  }

  useRegisterAppliedFilters({
    values: filters,
    defaults: CLEARABLE_FILTERS,
    onClear: () => {
      resetFilterFields(setValue, CLEARABLE_FILTERS)
      applyFilter()
    },
  })

  return (
    <section>
      <form className={styles.form}>
        <div className={styles.field}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => {
              const defaultValue =
                defaultValues?.project ??
                project ??
                projects?.find((option) => !option.is_demo)?.value ??
                projects?.[0]?.value

              return (
                <BpSelect
                  {...fieldProps}
                  Icon={ProjectIcon}
                  bottomSection={<AddProjectButton />}
                  defaultValue={defaultValue}
                  disabled={projectsLoading || projectsFetching}
                  fieldSize='lg'
                  loading={projectsLoading || projectsFetching}
                  options={projects}
                  title={t('fields.project')}
                  onChange={(e) => {
                    const previousProject = getValues('project')
                    const option = projects?.find((option) => option.value === e.target.value)

                    if (option) {
                      dispatch(setProject(option))
                    }

                    onChange(e)
                    if (previousProject && e.target.value !== previousProject) {
                      setValue('country', '')
                      setValue('keyword', '')
                      setValue('page_url', '')
                    }
                    applyFilter()
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='project'
          />
        </div>
        <div className={styles.field}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => {
              const defaultValue = defaultValues?.country
                ? defaultValues.country
                : country
                  ? country
                  : countries && countries[0].value

              return (
                <BpSelect
                  {...fieldProps}
                  Icon={CountryIcon}
                  bottomSection={<EditCountriesButton project={{ value: project, isDemo }} />}
                  defaultValue={defaultValue}
                  disabled={countriesLoading || countriesFetching}
                  fieldSize='lg'
                  loading={countriesLoading || countriesFetching}
                  optionRender={renderCountryOption}
                  options={countries}
                  title={t('fields.country')}
                  onChange={(e) => {
                    onChange(e)
                    applyFilter()
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='country'
          />
        </div>
        <div className={styles.field}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => {
              const defaultValue = defaultValues?.engine_id
                ? defaultValues.engine_id
                : engine_id
                  ? engine_id
                  : engines && engines[0].value

              return (
                <BpSelect
                  {...fieldProps}
                  Icon={SearchEngineIcon}
                  defaultValue={defaultValue}
                  disabled={enginesLoading || enginesFetching}
                  fieldSize='lg'
                  loading={enginesLoading || enginesFetching}
                  options={engines}
                  title={t('fields.searchEngine')}
                  onChange={(e) => {
                    onChange(e)
                    applyFilter()
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='engine_id'
          />
        </div>
        <div className={styles.filterDropWrap}>
          <FilterDrop
            values={getValues(['dateRange', 'keyword', 'page_url'])}
            onClear={() => {
              setValue('keyword', '')
              setValue('page_url', '')
            }}
            onHide={() => restoreAppliedValues(getValues, setValue, filters)}
            onSubmit={applyFilter}
          >
            <Controller
              render={({ field: { onChange, value } }) => {
                return (
                  <BpDatePicker
                    defaultValue={[
                      moment(value, DEFAULT_DATE_FORMAT),
                      moment(value, DEFAULT_RANGE_FORMAT),
                    ]}
                    label={t('fields.date')}
                    onChange={(e, t) => onChange(`${t[0]} - ${t[1]}`)}
                    onOpenChange={(e) => dispatch(setCanCloseDropdown(!e))}
                  />
                )
              }}
              control={control}
              name='dateRange'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  Icon={KeywordIcon}
                  enableChipLoad={false}
                  label={t('fields.keyword')}
                  loading={keywordsLoading || keywordsFetching}
                  options={withSelectedOptions('keyword', keywords, value)}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onSearch={setSearch('keyword')}
                ></BpSelect>
              )}
              control={control}
              name='keyword'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  Icon={UrlIcon}
                  enableChipLoad={false}
                  label={t('fields.pageUrl')}
                  loading={landingsLoading || landingsFetching}
                  options={withSelectedOptions('landing', landings, value)}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onSearch={setSearch('page_url')}
                ></BpSelect>
              )}
              control={control}
              name='page_url'
            />
          </FilterDrop>
        </div>
      </form>
      <div className={styles.chipsWrap}>
        <FilterChips
          clearCallBack={(field: any) => {
            setValue(field, '')
            applyFilter()
          }}
          items={[
            {
              title: t('fields.keyword'),
              field: 'keyword',
              value: keyword,
              data: withSelectedOptions('keyword', keywords, keyword),
              type: 'multiselect',
            },
            {
              title: t('fields.pageUrl'),
              field: 'page_url',
              value: page_url,
              data: withSelectedOptions('landing', landings, page_url),
              type: 'multiselect',
            },
          ]}
          applyCallBack={applyFilter}
        />
        {menuLayout === 'sidebar' && <ActionsSlot />}
      </div>
      {isDemo && (
        <div className={styles.demoBanner}>
          <svg
            className={styles.demoIcon}
            fill='none'
            height='20'
            viewBox='0 0 20 20'
            width='20'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              clipRule='evenodd'
              d='M5.67678 1.76256C6.00497 1.43437 6.45009 1.25 6.91421 1.25H13.0858C13.5499 1.25 13.995 1.43437 14.3232 1.76256L18.2374 5.67678C18.5656 6.00497 18.75 6.45009 18.75 6.91421V13.0858C18.75 13.5499 18.5656 13.995 18.2374 14.3232L14.3232 18.2374C13.995 18.5656 13.5499 18.75 13.0858 18.75H6.91421C6.45009 18.75 6.00497 18.5656 5.67678 18.2374L1.76256 14.3232C1.43437 13.995 1.25 13.5499 1.25 13.0858V6.91421C1.25 6.45009 1.43437 6.00497 1.76256 5.67678L5.67678 1.76256ZM6.91421 2.75C6.84791 2.75 6.78432 2.77634 6.73744 2.82322L2.82322 6.73744C2.77634 6.78432 2.75 6.84791 2.75 6.91421V13.0858C2.75 13.1521 2.77634 13.2157 2.82322 13.2626L6.73744 17.1768C6.78432 17.2237 6.84791 17.25 6.91421 17.25H13.0858C13.1521 17.25 13.2157 17.2237 13.2626 17.1768L17.1768 13.2626C17.2237 13.2157 17.25 13.1521 17.25 13.0858V6.91421C17.25 6.84791 17.2237 6.78432 17.1768 6.73744L13.2626 2.82322C13.2157 2.77634 13.1521 2.75 13.0858 2.75H6.91421ZM10 8.25C10.4142 8.25 10.75 8.58579 10.75 9V13C10.75 13.4142 10.4142 13.75 10 13.75C9.58579 13.75 9.25 13.4142 9.25 13V9C9.25 8.58579 9.58579 8.25 10 8.25ZM10 7.5C10.4142 7.5 10.75 7.16421 10.75 6.75C10.75 6.33579 10.4142 6 10 6C9.58579 6 9.25 6.33579 9.25 6.75C9.25 7.16421 9.58579 7.5 10 7.5Z'
              fill='#00326D'
              fillRule='evenodd'
            />
          </svg>
          <p className={styles.demoText}>
            <Trans i18nKey='other.demoProjectSelected'>
              <NavLink className={styles.demoLink} to='/projects' />
            </Trans>
          </p>
        </div>
      )}
    </section>
  )
}

export const SerpPresenceFilters = withSearchParams<
  IWithSearchParamsProps<ISerpPresenceFilter>,
  ISerpPresenceFilter
>(Filter, selectSerpPresenceFilters)
